/**
 * TrainingCompliancePage — Generic training compliance tracking
 *
 * Used by: CFSS, ALF, PCA, construction (OSHA), etc.
 * Training requirements come from task groups.
 *
 * Route: /{industry}/training
 * Scope: training:view
 */

import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBook,
  IconCertificate,
  IconCheck,
  IconClock,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_TRAINING_STATUS = gql`
  query GetTrainingStatus($taskGroupRef: String, $status: TaskStatus, $limit: Int) {
    tasks(taskGroupRef: $taskGroupRef, status: $status, limit: $limit) {
      id
      name
      description
      status
      deadlineAt
      completedAt
      assignedActorId
      priority
    }
  }
`;

interface TrainingTask {
  id: string;
  name: string;
  description: string | null;
  status: string;
  deadlineAt: string | null;
  completedAt: string | null;
  assignedActorId: string | null;
  priority: string;
}

interface TrainingCompliancePageProps {
  title?: string;
  subtitle?: string;
  taskGroupRef?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getDaysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const today = new Date();
  return Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getUrgencyColor(daysUntil: number | null): string {
  if (daysUntil === null) return 'gray';
  if (daysUntil < 0) return 'red';
  if (daysUntil <= 14) return 'orange';
  if (daysUntil <= 30) return 'yellow';
  return 'green';
}

export function TrainingCompliancePage({
  title = 'Training Compliance',
  subtitle = 'Required trainings and certifications',
  taskGroupRef = 'training',
  scope = 'training:view',
}: TrainingCompliancePageProps) {
  const [activeTab, setActiveTab] = useState<string | null>('required');
  const canView = useHasScope(scope);

  const { data: requiredData, loading: requiredLoading } = useQuery<{ tasks: TrainingTask[] }>(
    GET_TRAINING_STATUS,
    { variables: { taskGroupRef, status: 'PENDING', limit: 100 } }
  );

  const { data: completedData, loading: completedLoading } = useQuery<{ tasks: TrainingTask[] }>(
    GET_TRAINING_STATUS,
    { variables: { taskGroupRef, status: 'COMPLETED', limit: 100 } }
  );

  const loading = requiredLoading || completedLoading;
  const requiredTasks = requiredData?.tasks ?? [];
  const completedTasks = completedData?.tasks ?? [];

  const overdueCount = requiredTasks.filter(t => {
    const days = getDaysUntil(t.deadlineAt);
    return days !== null && days < 0;
  }).length;

  const dueSoonCount = requiredTasks.filter(t => {
    const days = getDaysUntil(t.deadlineAt);
    return days !== null && days >= 0 && days <= 30;
  }).length;

  const totalRequired = requiredTasks.length + completedTasks.length;
  const compliancePercent = totalRequired > 0 ? Math.round((completedTasks.length / totalRequired) * 100) : 100;

  if (loading) {
    return <Center h="60vh"><Loader size="lg" /></Center>;
  }

  if (!canView) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to view training records.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="cyan">
            <IconBook size={20} />
          </ThemeIcon>
          <Stack gap={0}>
            <Title order={2}>{title}</Title>
            <Text size="sm" c="dimmed">{subtitle}</Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 4 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Compliance</Text>
              <RingProgress
                size={48}
                thickness={4}
                roundCaps
                sections={[{ value: compliancePercent, color: compliancePercent >= 90 ? 'green' : compliancePercent >= 70 ? 'yellow' : 'red' }]}
                label={<Text size="xs" ta="center" fw={700}>{compliancePercent}%</Text>}
              />
            </Group>
            <Text size="xl" fw={700}>{completedTasks.length}/{totalRequired}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Overdue</Text>
              <ThemeIcon size="sm" variant="light" color="red"><IconAlertTriangle size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={overdueCount > 0 ? 'red' : undefined}>{overdueCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Due Soon</Text>
              <ThemeIcon size="sm" variant="light" color="orange"><IconClock size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={dueSoonCount > 0 ? 'orange' : undefined}>{dueSoonCount}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Completed</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCertificate size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{completedTasks.length}</Text>
          </Card>
        </SimpleGrid>

        {overdueCount > 0 && (
          <Alert color="red" title="Overdue Training" icon={<IconAlertTriangle />}>
            {overdueCount} required training(s) are past their deadline.
          </Alert>
        )}

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="required" leftSection={<IconClock size={14} />}>Required ({requiredTasks.length})</Tabs.Tab>
            <Tabs.Tab value="completed" leftSection={<IconCheck size={14} />}>Completed ({completedTasks.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="required" pt="md">
            {requiredTasks.length === 0 ? (
              <Alert color="green" title="All Caught Up" icon={<IconCheck />}>All required trainings have been completed.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Training</Table.Th>
                      <Table.Th>Assigned To</Table.Th>
                      <Table.Th>Deadline</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {requiredTasks.map(task => {
                      const daysUntil = getDaysUntil(task.deadlineAt);
                      return (
                        <Table.Tr key={task.id}>
                          <Table.Td>
                            <Stack gap={0}>
                              <Text size="sm" fw={500}>{task.name}</Text>
                              {task.description && <Text size="xs" c="dimmed" lineClamp={1}>{task.description}</Text>}
                            </Stack>
                          </Table.Td>
                          <Table.Td><Text size="sm">{task.assignedActorId ?? 'Unassigned'}</Text></Table.Td>
                          <Table.Td><Text size="sm">{formatDate(task.deadlineAt)}</Text></Table.Td>
                          <Table.Td>
                            <Badge color={getUrgencyColor(daysUntil)} size="sm">
                              {daysUntil === null ? 'No deadline' : daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil} days`}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="completed" pt="md">
            {completedTasks.length === 0 ? (
              <Alert color="gray" title="No Completed Trainings" icon={<IconBook />}>No training records found.</Alert>
            ) : (
              <Paper withBorder>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Training</Table.Th>
                      <Table.Th>Assigned To</Table.Th>
                      <Table.Th>Completed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {completedTasks.map(task => (
                      <Table.Tr key={task.id}>
                        <Table.Td><Text size="sm" fw={500}>{task.name}</Text></Table.Td>
                        <Table.Td><Text size="sm">{task.assignedActorId ?? 'Unknown'}</Text></Table.Td>
                        <Table.Td><Text size="sm">{formatDate(task.completedAt)}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default TrainingCompliancePage;
